import { normalize, getScheduleEntry } from "./scheduleUtils";

/**
 * Converts the currentWeek value (number or string like "3") to a plain integer.
 * Used in many places throughout MyLeague where currentWeek might be either type.
 */
export function getCurrentWeekNumber(currentWeek) {
  if (typeof currentWeek === "number") return currentWeek;
  if (!currentWeek || typeof currentWeek !== "string") return 1;
  const weekMatch = currentWeek.match(/\d+/);
  return weekMatch ? parseInt(weekMatch[0]) : 1;
}

/**
 * Returns true if any of a member's owned teams has a live game this week.
 * Only meaningful when viewMode === 'current'.
 */
export function getMemberLiveStatus(
  member,
  { viewMode, scheduleData, scheduleLoading, allTeams }
) {
  if (viewMode !== "current") return false;

  const lineup = member.lineup || {};
  const starters = Array.isArray(lineup.starters) ? lineup.starters : [];
  const bench = Array.isArray(lineup.bench) ? lineup.bench : [];
  const ownedTeams = [...starters, ...bench].filter(
    (t) => typeof t === "string" && t.trim() !== ""
  );

  return ownedTeams.some((teamName) => {
    const scheduleGame = getScheduleEntry(scheduleData, teamName);
    if (scheduleGame && !scheduleLoading) {
      return scheduleGame.gameStatus === "in_progress" || scheduleGame.hasLiveGame;
    }
    const team = allTeams[normalize(teamName)];
    return (
      team?.currentSeason?.gameStatus === "in_progress" ||
      team?.currentSeason?.hasLiveGame
    );
  });
}

// The lineup always has 5 starter slots (WeeklyLineupManager seeds
// Array(5).fill(null)), whether or not a manager has filled all of them. An
// empty slot never counts as played, which is what keeps this an honest
// signal of how much of the lineup has resolved rather than starting every
// manager at a full count regardless of how many teams they actually started.
const STARTER_SLOTS = 5;

/**
 * How many of a member's 5 starter slots are complete vs. still in progress
 * this week. Split rather than a single "played" count because WK already
 * carries live, provisional points the moment a game kicks off (espn.js
 * scores every 2-minute tick, not only on final) — a starter mid-game is
 * already moving that number, so lumping it in with "hasn't started" would
 * make the indicator lag behind the score it sits next to. Complete is kept
 * separate from in-progress so a manager can tell "this is locked in" from
 * "this could still swing."
 *
 * Only meaningful when viewMode === 'current': a past week's games are all
 * long since final, so this would just read complete: 5 (or however many
 * slots were filled) for every row.
 */
export function getStartersPlayedCount(
  member,
  { viewMode, scheduleData, allTeams }
) {
  if (viewMode !== "current") return { complete: 0, inProgress: 0, total: STARTER_SLOTS };

  const starters = Array.isArray(member.lineup?.starters) ? member.lineup.starters : [];

  let complete = 0;
  let inProgress = 0;
  for (const teamName of starters) {
    if (typeof teamName !== "string" || teamName.trim() === "") continue;
    const scheduleGame = getScheduleEntry(scheduleData, teamName);
    const team = allTeams[normalize(teamName)];
    const isComplete = scheduleGame ? !!scheduleGame.gameComplete : !!team?.currentSeason?.gameComplete;
    if (isComplete) {
      complete++;
      continue;
    }
    const isLive = scheduleGame
      ? scheduleGame.gameStatus === "in_progress" || scheduleGame.hasLiveGame
      : team?.currentSeason?.gameStatus === "in_progress" || team?.currentSeason?.hasLiveGame;
    if (isLive) inProgress++;
  }

  return { complete, inProgress, total: STARTER_SLOTS };
}

// Reduces a member's weekly_lineups rows for the whole season down to the
// two numbers the standings tiebreak needs: the single best week they have
// ever put up, and the captain's earned points in that same week (already
// carrying the 2x/5x multiplier, from team_points[captain]). A week that ties
// the current best keeps whichever had the stronger captain performance, so
// the pair always describes one specific week rather than mixing the best
// score with an unrelated week's captain.
export function getBestWeekStats(seasonLineupRows) {
  let bestWeekPoints = 0;
  let bestWeekCaptainPoints = 0;
  (seasonLineupRows || []).forEach((row) => {
    const points = row.points ?? 0;
    const captainPoints = row.captain ? (row.team_points || {})[row.captain] ?? 0 : 0;
    if (points > bestWeekPoints || (points === bestWeekPoints && captainPoints > bestWeekCaptainPoints)) {
      bestWeekPoints = points;
      bestWeekCaptainPoints = captainPoints;
    }
  });
  return { bestWeekPoints, bestWeekCaptainPoints };
}

/**
 * Returns the sorted list of members to display based on viewMode.
 * Current mode: sorted by cumulative points, then each manager's best single
 * week of the season, then that week's captain performance.
 * Historical mode: returns the pre-ranked weekly snapshot.
 */
// Three-stage tiebreak: season points, then the best week either manager has
// put up all season (bestWeekPoints), then the captain's earned points in
// that same week (bestWeekCaptainPoints), then team name so the order is at
// least stable. Both come from getBestWeekStats over weekly_lineups
// (useLeagueData/useUserLeagues): league_members has neither column, so a
// naive tiebreak compared undefined to undefined and tied managers landed in
// whatever order Postgres returned them, reshuffling between loads.
export function getDisplayData({ viewMode, members, weeklyStandings, selectedWeek }) {
  if (viewMode === "current") {
    return [...members].sort((a, b) => {
      const aPoints = a.points ?? 0;
      const bPoints = b.points ?? 0;
      if (bPoints !== aPoints) return bPoints - aPoints;

      const aBestWeek = a.bestWeekPoints ?? 0;
      const bBestWeek = b.bestWeekPoints ?? 0;
      if (bBestWeek !== aBestWeek) return bBestWeek - aBestWeek;

      const aCaptain = a.bestWeekCaptainPoints ?? 0;
      const bCaptain = b.bestWeekCaptainPoints ?? 0;
      if (bCaptain !== aCaptain) return bCaptain - aCaptain;

      return (a.teamName || "").localeCompare(b.teamName || "");
    });
  }
  return weeklyStandings[selectedWeek] || [];
}
